import {useCallback, useEffect, useRef, useState} from 'react';
import {cn} from '@tinct/core-utils';
import {GameConfig} from './game-types';
import {GameEngine} from './game-engine';
import {characterUrls, GameRenderer, loadAssets} from './game-renderer';

export interface GameProps {
  className?: string;
}

const pickCharacterUrl = () =>
  characterUrls[Math.floor(Math.random() * characterUrls.length)];

export const Game = ({ className }: GameProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const birdRef = useRef<HTMLImageElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const rendererRef = useRef<GameRenderer | null>(null);
  const animFrameRef = useRef<number>(0);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [characterUrl] = useState(pickCharacterUrl);

  // Initialize engine and renderer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;

    loadAssets().then((assets) => {
      if (cancelled) return;
      const engine = new GameEngine();
      const renderer = new GameRenderer(ctx, assets);
      engineRef.current = engine;
      rendererRef.current = renderer;
      setAssetsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Game loop
  useEffect(() => {
    if (!assetsLoaded) return;

    const engine = engineRef.current;
    const renderer = rendererRef.current;
    const bird = birdRef.current;
    if (!engine || !renderer || !bird) return;

    const W = GameConfig.CANVAS_WIDTH;
    const H = GameConfig.CANVAS_HEIGHT;

    let lastTime = 0;

    const gameLoop = (timestamp: number) => {
      if (lastTime === 0) lastTime = timestamp;
      const dt = Math.min(
        (timestamp - lastTime) / GameConfig.TARGET_FRAME_TIME,
        GameConfig.MAX_DELTA_FACTOR,
      );
      lastTime = timestamp;

      engine.update(dt);

      switch (engine.state) {
        case 'idle':
          renderer.renderIdleScreen(engine, dt);
          break;
        case 'running':
          renderer.render(engine);
          break;
        case 'game-over':
          renderer.renderGameOver(engine);
          break;
      }

      // Position the bird <img> overlay using percentages
      // so it stays aligned with the CSS-scaled canvas
      const { player } = engine;
      const left = (player.x / W) * 100;
      const top = (player.y / H) * 100;
      const width = (player.width / W) * 100;
      const height = (player.height / H) * 100;

      bird.style.left = `${left}%`;
      bird.style.top = `${top}%`;
      bird.style.width = `${width}%`;
      bird.style.height = `${height}%`;

      animFrameRef.current = requestAnimationFrame(gameLoop);
    };

    animFrameRef.current = requestAnimationFrame(gameLoop);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [assetsLoaded]);

  // Keyboard input
  useEffect(() => {
    if (!assetsLoaded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const engine = engineRef.current;
      if (!engine) return;

      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        engine.jump();
      } else if (e.code === 'ArrowDown') {
        e.preventDefault();
        engine.duck(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const engine = engineRef.current;
      if (!engine) return;

      if (e.code === 'ArrowDown') {
        engine.duck(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [assetsLoaded]);

  const handleInteraction = useCallback(() => {
    engineRef.current?.jump();
  }, []);

  return (
    <div
      className={cn('relative select-none', className)}
      onClick={handleInteraction}
      onTouchStart={(e) => {
        e.preventDefault();
        handleInteraction();
      }}
    >
      <canvas
        ref={canvasRef}
        width={GameConfig.CANVAS_WIDTH}
        height={GameConfig.CANVAS_HEIGHT}
        className="w-full rounded-xl border border-border"
        style={{ imageRendering: 'pixelated', aspectRatio: '1000 / 300' }}
        tabIndex={0}
        aria-label="Tinct Runner Game"
      />
      {/* Bird rendered as a DOM img so the browser natively animates the GIF */}
      <img
        ref={birdRef}
        src={characterUrl}
        alt=""
        className="pointer-events-none absolute"
        style={{ imageRendering: 'pixelated' }}
      />
      {!assetsLoaded && (
        <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-muted">
          <span className="text-sm text-muted-foreground">Loading game...</span>
        </div>
      )}
    </div>
  );
};
